import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import AnimatedSection from '../components/AnimatedSection';
import { API_BASE, fallbackPortfolio } from '../data/fallback';

/* ---- Counter Hook ---- */
const useCounter = (target, duration = 2000, start = false) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (timestamp) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      const eased = 1 - Math.pow(1 - progress, 3);
      setCount(Math.floor(eased * target));
      if (progress < 1) requestAnimationFrame(step);
      else setCount(target);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return count;
};

/* ---- Counter Card ---- */
const CounterCard = ({ target, label, suffix = '+', icon, delay = 0 }) => {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const count = useCounter(target, 2000, visible);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setTimeout(() => setVisible(true), delay); observer.disconnect(); } },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [delay]);
  return (
    <div ref={ref} className="stat-card">
      <div style={{ fontSize: '2rem', marginBottom: '10px' }}>{icon}</div>
      <div className="stat-number">{count}{suffix}</div>
      <div className="stat-label">{label}</div>
    </div>
  );
};

/* ---- Hero Slider Data ---- */
const slides = [
  {
    bg: 'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1600&q=80',
    title: 'Welcome to SAANVI INNOVATIONS',
    desc: 'SAANVI INNOVATIONS is a software and web development company established in 2021. Our vision is to become a world leader in vertical IT solution and services. We deliver innovative, reliable and cost-effective solutions to our clients.',
    graphic: 'browser',
  },
  {
    bg: 'https://images.unsplash.com/photo-1561070791-2526d30994b5?w=1600&q=80',
    title: 'Experience SAANVI INNOVATIONS',
    desc: 'SAANVI INNOVATIONS — starting from creating a website to all web solutions in one place. We are offering web development service, software development, dynamic & responsive website design, e-commerce website development, digital marketing service and mobile application development.',
    graphic: 'wireframe',
  },
  {
    bg: 'https://images.unsplash.com/photo-1531403009284-440f080d1e12?w=1600&q=80',
    title: 'Web Designing and Development Company in India',
    desc: 'SAANVI INNOVATIONS is a Gwalior based web designing and development company providing innovative web solutions for your business. We bring you powerful web designing and developing solutions at the cost of guaranteed results, adhering to search engine guidelines.',
    graphic: 'code',
  },
];

/* ---- Neon Browser Graphic ---- */ // eslint-disable-line no-unused-vars
const BrowserGraphic = () => (
  <svg viewBox="0 0 320 220" width="320" height="220" style={{ filter: 'drop-shadow(0 0 12px #ff00aa)' }}>
    {/* outer frame */}
    <rect x="10" y="10" width="300" height="200" rx="12" fill="none" stroke="#ff00aa" strokeWidth="2.5" strokeDasharray="8 3" opacity="0.9"/>
    {/* title bar */}
    <rect x="10" y="10" width="300" height="34" rx="12" fill="none" stroke="#00d4ff" strokeWidth="2" opacity="0.8"/>
    {/* traffic lights */}
    <circle cx="32" cy="27" r="6" fill="none" stroke="#ff6b35" strokeWidth="2"/>
    <circle cx="52" cy="27" r="6" fill="none" stroke="#ff6b35" strokeWidth="2"/>
    <circle cx="72" cy="27" r="6" fill="none" stroke="#ff6b35" strokeWidth="2"/>
    {/* address bar */}
    <rect x="90" y="18" width="180" height="18" rx="9" fill="none" stroke="#00d4ff" strokeWidth="1.5" opacity="0.7"/>
    {/* inner content lines */}
    <rect x="30" y="60" width="120" height="8" rx="4" fill="#ff00aa" opacity="0.4"/>
    <rect x="30" y="78" width="260" height="5" rx="2.5" fill="#00d4ff" opacity="0.3"/>
    <rect x="30" y="92" width="220" height="5" rx="2.5" fill="#00d4ff" opacity="0.3"/>
    <rect x="30" y="106" width="240" height="5" rx="2.5" fill="#00d4ff" opacity="0.3"/>
    {/* second mini browser */}
    <rect x="50" y="130" width="220" height="65" rx="8" fill="none" stroke="#ff00aa" strokeWidth="2" opacity="0.7"/>
    <rect x="50" y="130" width="220" height="22" rx="8" fill="none" stroke="#00d4ff" strokeWidth="1.5" opacity="0.6"/>
    <circle cx="66" cy="141" r="4" fill="none" stroke="#ff6b35" strokeWidth="1.5"/>
    <circle cx="80" cy="141" r="4" fill="none" stroke="#ff6b35" strokeWidth="1.5"/>
    <circle cx="94" cy="141" r="4" fill="none" stroke="#ff6b35" strokeWidth="1.5"/>
    <rect x="66" y="162" width="160" height="4" rx="2" fill="#00d4ff" opacity="0.3"/>
    <rect x="66" y="174" width="130" height="4" rx="2" fill="#00d4ff" opacity="0.3"/>
    {/* animated dash border */}
    <rect x="10" y="10" width="300" height="200" rx="12" fill="none" stroke="#00d4ff" strokeWidth="1" opacity="0.3">
      <animate attributeName="stroke-dashoffset" from="0" to="800" dur="8s" repeatCount="indefinite"/>
      <animate attributeName="stroke-dasharray" values="0 800;400 400;0 800" dur="8s" repeatCount="indefinite"/>
    </rect>
  </svg>
);

/* ---- Wireframe Graphic ---- */
const WireframeGraphic = () => (
  <svg viewBox="0 0 320 220" width="320" height="220" style={{ filter: 'drop-shadow(0 0 10px #00d4ff)' }}>
    <rect x="10" y="10" width="300" height="200" rx="8" fill="none" stroke="#00d4ff" strokeWidth="2" opacity="0.8"/>
    {/* header */}
    <rect x="10" y="10" width="300" height="30" rx="8" fill="rgba(0,212,255,0.1)" stroke="#00d4ff" strokeWidth="1.5"/>
    {/* hero image placeholder */}
    <rect x="20" y="50" width="280" height="70" rx="4" fill="none" stroke="#ff6b35" strokeWidth="1.5" strokeDasharray="6 3"/>
    <line x1="20" y1="50" x2="300" y2="120" stroke="#ff6b35" strokeWidth="1" opacity="0.4"/>
    <line x1="300" y1="50" x2="20" y2="120" stroke="#ff6b35" strokeWidth="1" opacity="0.4"/>
    {/* content cols */}
    <rect x="20" y="132" width="85" height="60" rx="4" fill="none" stroke="#00d4ff" strokeWidth="1.5" opacity="0.7"/>
    <rect x="118" y="132" width="85" height="60" rx="4" fill="none" stroke="#00d4ff" strokeWidth="1.5" opacity="0.7"/>
    <rect x="216" y="132" width="84" height="60" rx="4" fill="none" stroke="#00d4ff" strokeWidth="1.5" opacity="0.7"/>
    {/* animated highlight */}
    <rect x="118" y="132" width="85" height="60" rx="4" fill="rgba(255,107,53,0.15)">
      <animate attributeName="opacity" values="0;1;0" dur="2s" repeatCount="indefinite"/>
    </rect>
    <text x="160" y="35" textAnchor="middle" fill="#00d4ff" fontSize="10" opacity="0.8">UI WIREFRAME</text>
  </svg>
);

/* ---- Code Graphic ---- */
const CodeGraphic = () => (
  <svg viewBox="0 0 320 220" width="320" height="220" style={{ filter: 'drop-shadow(0 0 10px #ff6b35)' }}>
    <rect x="10" y="10" width="300" height="200" rx="10" fill="rgba(0,0,0,0.4)" stroke="#ff6b35" strokeWidth="2"/>
    <rect x="10" y="10" width="300" height="28" rx="10" fill="rgba(255,107,53,0.2)" stroke="#ff6b35" strokeWidth="1.5"/>
    <circle cx="28" cy="24" r="5" fill="#ff5f56"/>
    <circle cx="44" cy="24" r="5" fill="#ffbd2e"/>
    <circle cx="60" cy="24" r="5" fill="#27c93f"/>
    <text x="20" y="56" fill="#ff6b35" fontSize="11" fontFamily="monospace" opacity="0.9">{'<html>'}</text>
    <text x="30" y="74" fill="#00d4ff" fontSize="11" fontFamily="monospace" opacity="0.9">{'  <body>'}</text>
    <text x="40" y="92" fill="#ffffff" fontSize="11" fontFamily="monospace" opacity="0.8">{'    <div class="hero">'}</text>
    <text x="50" y="110" fill="#ff00aa" fontSize="11" fontFamily="monospace" opacity="0.8">{'      SAANVI'}</text>
    <text x="50" y="128" fill="#ff00aa" fontSize="11" fontFamily="monospace" opacity="0.8">{'      INNOVATIONS'}</text>
    <text x="40" y="146" fill="#ffffff" fontSize="11" fontFamily="monospace" opacity="0.8">{'    </div>'}</text>
    <text x="30" y="164" fill="#00d4ff" fontSize="11" fontFamily="monospace" opacity="0.9">{'  </body>'}</text>
    <text x="20" y="182" fill="#ff6b35" fontSize="11" fontFamily="monospace" opacity="0.9">{'</html>'}</text>
    {/* blinking cursor */}
    <rect x="20" y="186" width="8" height="12" fill="#ff6b35">
      <animate attributeName="opacity" values="1;0;1" dur="1s" repeatCount="indefinite"/>
    </rect>
  </svg>
);

/* ---- WEBSITE watermark text ---- */
// eslint-disable-next-line no-unused-vars
const WebsiteWatermark = () => (
  <div style={{
    position: 'absolute', right: '5%', bottom: '10%',
    fontSize: 'clamp(3rem, 8vw, 6rem)',
    fontWeight: 900, letterSpacing: '0.05em',
    color: 'transparent',
    WebkitTextStroke: '2px rgba(0,212,255,0.35)',
    fontFamily: 'sans-serif',
    userSelect: 'none',
    animation: 'fadeInRight 1s ease 0.5s both',
    lineHeight: 1,
    zIndex: 2,
    pointerEvents: 'none',
  }}>
    WEB<br />
    <span style={{ WebkitTextStroke: '2px rgba(255,107,53,0.35)' }}>SITE</span>
  </div>
);

/* ---- Portfolio Data (fetched from API, fallback to static) ---- */

const services = [
  { icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>, title: 'Website Development', desc: 'Custom websites built with modern technologies for optimal performance and user experience.' },
  { icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M12 20h9"/><path d="M16.5 3.5a2.121 2.121 0 0 1 3 3L7 19l-4 1 1-4L16.5 3.5z"/></svg>, title: 'Website Design', desc: 'Stunning UI/UX designs that capture your brand essence and engage visitors effectively.' },
  { icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>, title: 'Digital Marketing', desc: 'Data-driven digital marketing strategies to grow your online presence and ROI.' },
  { icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>, title: 'SEO', desc: 'Search engine optimization to rank higher, drive organic traffic and increase conversions.' },
  { icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M18 20V10"/><path d="M12 20V4"/><path d="M6 20v-6"/></svg>, title: 'Social Media Optimization', desc: 'Optimize your social media profiles for maximum reach and engagement across platforms.' },
  { icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 4s-3 5-6 7c-3 2-8 2-11 0s-3-5 0-7 8-2 11 0 4 6 4 6"/><path d="M2 20l5-5"/></svg>, title: 'Social Media Marketing', desc: 'Strategic social media campaigns that build brand awareness and loyal customer communities.' },
];

// eslint-disable-next-line no-unused-vars
const clientNames = [
  'Global Hospital', 'Laxman Edu', 'Muktidham', 'JugadGuru',
  'RMS School', 'Dalmia Heart', 'Malwa Vikas', 'Sanskar Valley',
];

/* ──────────────────────────────────────────────────────────
   PROMOTION SECTION — Professional poster + video gallery
────────────────────────────────────────────────────────── */

const POSTER_DATA = [
  {
    id:'p1', badge:'Special Offer',
    headline:'Transform Your Business Online',
    sub:'Professional websites from ₹5,000. Fast delivery, no hidden fees — 5-star rated service by experts in Gwalior.',
    cta:'Get Free Quote', ctaLink:'/contact#quote',
    ctaSecondary:'View Pricing', ctaSecondaryLink:'/pricing',
    accent:'#e8603a',
    bg:'linear-gradient(135deg,#0f0c29 0%,#302b63 50%,#24243e 100%)',
    stats:[{num:'100+',label:'Websites Built'},{num:'50+',label:'Happy Clients'},{num:'5+',label:'Years Experience'}],
  },
  {
    id:'p2', badge:'Trusted Since 2021',
    headline:'200+ Projects Delivered Across India',
    sub:'From hospitals to schools, e-commerce to ERP — we build digital solutions that deliver real, measurable results.',
    cta:'View Our Portfolio', ctaLink:'/portfolio',
    ctaSecondary:'About Us', ctaSecondaryLink:'/about',
    accent:'#4fc3f7',
    bg:'linear-gradient(135deg,#1a1a0f 0%,#0f2027 50%,#203a43 100%)',
    stats:[{num:'200+',label:'Projects Done'},{num:'₹5K',label:'Starting Price'},{num:'24h',label:'Response Time'}],
  },
];

function PromotionSection({ apiBase }) {
  const [posterIdx,  setPosterIdx]  = useState(0);
  const [posterAnim, setPosterAnim] = useState(true);
  const [videos,     setVideos]     = useState([]);
  const [lightbox,   setLightbox]   = useState(null);
  const vidRefs = useRef({});

  /* fetch videos — combine promotion videos + portfolio home-videos */
  useEffect(() => {
    const fetchPromotionVideos = fetch(`${apiBase}/api/promotions`)
      .then(r => r.json())
      .then(d => {
        if (!d.success) return [];
        // Only video-type promotions with a media_url
        return d.data
          .filter(p => p.type === 'video' && p.media_url && p.media_url.trim())
          .map(p => ({
            id:         `promo_${p.id}`,
            title:      p.title,
            video_url:  p.media_url,
            image_url:  null,
            is_product: 0,
            color:      '#16165a',
            _source:    'promotion',
          }));
      })
      .catch(() => []);

    const fetchPortfolioVideos = fetch(`${apiBase}/api/portfolio/home-videos`)
      .then(r => r.json())
      .then(d => (d.success ? d.data : []))
      .catch(() => []);

    Promise.all([fetchPromotionVideos, fetchPortfolioVideos]).then(([promoVids, portVids]) => {
      // Promotion videos first, then portfolio videos, max 6 total
      const combined = [...promoVids, ...portVids].slice(0, 6);
      setVideos(combined);
    });
  }, [apiBase]);

  /* auto-advance poster */
  useEffect(() => {
    const t = setInterval(() => {
      setPosterAnim(false);
      setTimeout(() => { setPosterIdx(i => (i + 1) % POSTER_DATA.length); setPosterAnim(true); }, 200);
    }, 6000);
    return () => clearInterval(t);
  }, []);

  const goTo = (idx) => {
    setPosterAnim(false);
    setTimeout(() => { setPosterIdx(idx); setPosterAnim(true); }, 200);
  };

  const handleVidHover = (id) => {
    const el = vidRefs.current[id];
    if (el) { el.currentTime = 0; el.play().catch(() => {}); }
  };
  const handleVidLeave = (id) => {
    const el = vidRefs.current[id];
    if (el) { el.pause(); el.currentTime = 0; }
  };

  const resolveUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http')) return url;
    // Promotion video files are in /uploads/promotions/
    if (url.startsWith('/uploads/promotions/')) {
      const fn = url.split('/').pop();
      return `${apiBase}/api/promotions/stream/${fn}`;
    }
    // Portfolio video files
    if (url.startsWith('/uploads/portfolio/videos/')) {
      const fn = url.split('/').pop();
      return `${apiBase}/stream/portfolio/${fn}`;
    }
    return `${apiBase}${url}`;
  };

  const getEmbed = (url) => {
    const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([A-Za-z0-9_-]{11})/);
    if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
    const vm = url.match(/vimeo\.com\/(\d+)/);
    if (vm)  return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
    return null;
  };

  const poster = POSTER_DATA[posterIdx];

  return (
    <section style={{ padding:'96px 0', background:'#07071a', position:'relative', overflow:'hidden' }}>
      <style>{`
        .promo-bg{position:absolute;inset:0;background-image:radial-gradient(rgba(255,255,255,0.03) 1px,transparent 1px);background-size:36px 36px;pointer-events:none;}
        .promo-card{border-radius:22px;overflow:hidden;display:grid;grid-template-columns:1.1fr 0.9fr;min-height:400px;}
        .promo-card.in{opacity:1;transform:translateY(0);transition:opacity 0.22s,transform 0.22s;}
        .promo-card.out{opacity:0;transform:translateY(10px);}
        .promo-left{padding:56px 52px;display:flex;flex-direction:column;justify-content:center;position:relative;z-index:2;}
        .promo-badge{display:inline-flex;align-items:center;gap:6px;padding:5px 14px;border-radius:9999px;font-size:0.68rem;font-weight:800;letter-spacing:0.1em;text-transform:uppercase;margin-bottom:22px;width:fit-content;}
        .promo-headline{font-size:clamp(1.6rem,3vw,2.5rem);font-weight:800;color:white;line-height:1.16;letter-spacing:-0.03em;margin-bottom:16px;}
        .promo-sub{font-size:0.95rem;color:rgba(255,255,255,0.62);line-height:1.85;margin-bottom:32px;max-width:440px;}
        .promo-btns{display:flex;gap:12px;flex-wrap:wrap;}
        .promo-btn-p{display:inline-flex;align-items:center;gap:7px;padding:12px 26px;background:#e8603a;color:white;border-radius:9999px;font-weight:700;font-size:0.875rem;text-decoration:none;border:none;cursor:pointer;box-shadow:0 4px 18px rgba(232,96,58,0.4);transition:all 0.25s;}
        .promo-btn-p:hover{background:#d4532e;transform:translateY(-2px);box-shadow:0 8px 26px rgba(232,96,58,0.5);}
        .promo-btn-g{display:inline-flex;align-items:center;gap:7px;padding:11px 22px;background:transparent;color:rgba(255,255,255,0.75);border-radius:9999px;font-weight:600;font-size:0.875rem;text-decoration:none;border:1.5px solid rgba(255,255,255,0.22);transition:all 0.22s;}
        .promo-btn-g:hover{border-color:rgba(255,255,255,0.55);color:white;transform:translateY(-1px);}
        .promo-stats{display:flex;gap:28px;margin-top:36px;padding-top:28px;border-top:1px solid rgba(255,255,255,0.08);}
        .promo-stat-num{font-size:1.6rem;font-weight:800;line-height:1;letter-spacing:-0.04em;}
        .promo-stat-lbl{font-size:0.68rem;color:rgba(255,255,255,0.4);font-weight:600;text-transform:uppercase;letter-spacing:0.08em;margin-top:3px;}
        .promo-right{position:relative;overflow:hidden;display:flex;align-items:center;justify-content:center;}
        .promo-nav{display:flex;align-items:center;justify-content:center;gap:14px;margin-top:28px;}
        .promo-ndot{width:7px;height:7px;border-radius:50%;background:rgba(255,255,255,0.22);border:none;cursor:pointer;transition:all 0.3s;}
        .promo-ndot.active{background:#e8603a;width:24px;border-radius:4px;}
        .promo-narr{width:36px;height:36px;border-radius:50%;background:rgba(255,255,255,0.06);border:1px solid rgba(255,255,255,0.14);color:white;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:all 0.22s;}
        .promo-narr:hover{background:#e8603a;border-color:#e8603a;}
        /* Video grid */
        .promo-vgrid{display:grid;grid-template-columns:repeat(3,1fr);gap:16px;margin-top:64px;}
        .promo-vc{position:relative;border-radius:14px;overflow:hidden;aspect-ratio:16/9;cursor:pointer;background:#111135;box-shadow:0 4px 20px rgba(0,0,0,0.4);transition:transform 0.3s cubic-bezier(0.34,1.56,0.64,1),box-shadow 0.3s;}
        .promo-vc:hover{transform:translateY(-6px) scale(1.02);box-shadow:0 16px 40px rgba(0,0,0,0.6);z-index:2;}
        .promo-vc video,.promo-vc img{width:100%;height:100%;object-fit:cover;display:block;}
        .promo-vov{position:absolute;inset:0;background:linear-gradient(to top,rgba(0,0,0,0.75) 0%,rgba(0,0,0,0.2) 50%,transparent 100%);display:flex;flex-direction:column;align-items:center;justify-content:center;transition:opacity 0.3s;}
        .promo-vc:hover .promo-vov{opacity:0;}
        .promo-vpb{width:52px;height:52px;border-radius:50%;background:rgba(232,96,58,0.9);display:flex;align-items:center;justify-content:center;box-shadow:0 4px 20px rgba(232,96,58,0.5);animation:vpPulse 2s ease-in-out infinite;}
        @keyframes vpPulse{0%,100%{transform:scale(1)}50%{transform:scale(1.08)}}
        .promo-vtit{position:absolute;bottom:12px;left:12px;right:12px;color:white;font-size:0.8rem;font-weight:700;line-height:1.3;text-shadow:0 1px 6px rgba(0,0,0,0.7);transition:opacity 0.3s;}
        .promo-vc:hover .promo-vtit{opacity:0;}
        .promo-vexp{position:absolute;top:10px;right:10px;width:30px;height:30px;border-radius:8px;background:rgba(232,96,58,0.9);display:flex;align-items:center;justify-content:center;opacity:0;transition:opacity 0.25s;z-index:3;}
        .promo-vc:hover .promo-vexp{opacity:1;}
        /* Lightbox */
        .promo-lb{position:fixed;inset:0;z-index:99999;background:rgba(0,0,0,0.92);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;animation:lbIn 0.25s ease both;}
        @keyframes lbIn{from{opacity:0;transform:scale(0.95)}to{opacity:1;transform:scale(1)}}
        .promo-lbi{width:min(900px,94vw);aspect-ratio:16/9;background:#000;border-radius:16px;overflow:hidden;position:relative;box-shadow:0 32px 80px rgba(0,0,0,0.8);}
        .promo-lbi video,.promo-lbi iframe{width:100%;height:100%;border:none;object-fit:contain;}
        .promo-lbc{position:fixed;top:24px;right:24px;z-index:100000;width:44px;height:44px;border-radius:50%;background:rgba(255,255,255,0.12);border:1.5px solid rgba(255,255,255,0.2);color:white;display:flex;align-items:center;justify-content:center;cursor:pointer;transition:background 0.2s;}
        .promo-lbc:hover{background:#e8603a;border-color:#e8603a;}
        @media(max-width:900px){.promo-card{grid-template-columns:1fr}.promo-right{display:none}.promo-left{padding:36px 24px}}
        @media(max-width:768px){.promo-vgrid{grid-template-columns:repeat(2,1fr)}}
        @media(max-width:480px){.promo-vgrid{grid-template-columns:1fr}}
      `}</style>

      <div className="promo-bg" />
      {/* Glow orbs */}
      <div style={{position:'absolute',width:500,height:500,borderRadius:'50%',background:'radial-gradient(circle,rgba(232,96,58,0.07),transparent)',top:'-120px',left:'-80px',filter:'blur(60px)',pointerEvents:'none'}} />
      <div style={{position:'absolute',width:400,height:400,borderRadius:'50%',background:'radial-gradient(circle,rgba(79,195,247,0.06),transparent)',bottom:'-100px',right:'-60px',filter:'blur(60px)',pointerEvents:'none'}} />

      <div className="container" style={{position:'relative',zIndex:1}}>

        {/* Section header */}
        <AnimatedSection className="section-header">
          <span className="section-tag">Why Choose Us</span>
          <h2 className="section-title">Why Choose <span>Saanvi Innovation</span></h2>
          <div className="section-divider"><span></span><span></span><span></span></div>
          <p className="section-subtitle" style={{color:'rgba(255,255,255,0.5)'}}>Trusted by 50+ businesses across India for professional digital solutions since 2021.</p>
        </AnimatedSection>

        {/* Poster slider */}
        <AnimatedSection animation="reveal">
          <div className={`promo-card ${posterAnim?'in':'out'}`} style={{background:poster.bg}}>
            <div className="promo-left">
              <div className="promo-badge" style={{background:`${poster.accent}18`,border:`1px solid ${poster.accent}55`,color:poster.accent}}>
                <svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor"><polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2"/></svg>
                {poster.badge}
              </div>
              <h2 className="promo-headline">{poster.headline}</h2>
              <p className="promo-sub">{poster.sub}</p>
              <div className="promo-btns">
                <Link to={poster.ctaLink} className="promo-btn-p">
                  {poster.cta}
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
                <Link to={poster.ctaSecondaryLink} className="promo-btn-g">{poster.ctaSecondary}</Link>
              </div>
              <div className="promo-stats">
                {poster.stats.map((s,i) => (
                  <div key={i}>
                    <div className="promo-stat-num" style={{color:poster.accent}}>{s.num}</div>
                    <div className="promo-stat-lbl">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>
            {/* Right decorative */}
            <div className="promo-right">
              <svg viewBox="0 0 380 360" width="380" height="360" style={{opacity:0.9}}>
                <defs>
                  <radialGradient id="prg1" cx="50%" cy="50%" r="50%">
                    <stop offset="0%" stopColor={poster.accent} stopOpacity="0.15"/>
                    <stop offset="100%" stopColor={poster.accent} stopOpacity="0"/>
                  </radialGradient>
                  <linearGradient id="plg1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={poster.accent} stopOpacity="0.9"/>
                    <stop offset="100%" stopColor={poster.accent} stopOpacity="0.4"/>
                  </linearGradient>
                </defs>
                <circle cx="190" cy="180" r="140" fill="url(#prg1)"/>
                <rect x="60" y="50" width="260" height="160" rx="18" fill="rgba(255,255,255,0.06)" stroke="rgba(255,255,255,0.1)" strokeWidth="1"/>
                <rect x="76" y="68" width="100" height="10" rx="5" fill="rgba(255,255,255,0.35)"/>
                <rect x="76" y="86" width="180" height="7" rx="3.5" fill="rgba(255,255,255,0.15)"/>
                <rect x="76" y="100" width="140" height="7" rx="3.5" fill="rgba(255,255,255,0.1)"/>
                {[0,1,2,3,4].map(i => (
                  <rect key={i} x={76+i*24} y={168-(i%3===1?14:i%3===0?8:0)} width="18" height={40+(i%3===1?14:i%3===0?8:0)} rx="4" fill="url(#plg1)" opacity={0.6+i*0.08}/>
                ))}
                <rect x="76" y="178" width="84" height="22" rx="11" fill={poster.accent}/>
                <text x="94" y="193" fill="white" fontSize="10" fontWeight="700">₹5,000+</text>
                <circle cx="230" cy="78" r="18" fill="rgba(34,197,94,0.15)" stroke="rgba(34,197,94,0.4)" strokeWidth="1.5"/>
                <path d="M222 78l5 6 10-12" stroke="#4ade80" strokeWidth="2" fill="none" strokeLinecap="round"/>
                <rect x="80" y="230" width="220" height="90" rx="14" fill="rgba(255,255,255,0.04)" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
                <rect x="96" y="248" width="80" height="8" rx="4" fill="rgba(255,255,255,0.25)"/>
                <rect x="96" y="264" width="150" height="6" rx="3" fill="rgba(255,255,255,0.12)"/>
                {[0,1,2,3].map(i=>(
                  <g key={i}>
                    <circle cx={96+i*30} cy={295} r="10" fill="rgba(255,255,255,0.08)" stroke="rgba(255,255,255,0.15)" strokeWidth="1"/>
                    <text x={91+i*30} y={299} fill="rgba(255,255,255,0.6)" fontSize="8">{['W','D','M','E'][i]}</text>
                  </g>
                ))}
                <circle cx="330" cy="100" r="6" fill={poster.accent} opacity="0.6"><animate attributeName="cy" values="100;88;100" dur="3s" repeatCount="indefinite"/></circle>
                <circle cx="50" cy="230" r="5" fill="rgba(79,195,247,0.7)"><animate attributeName="cy" values="230;218;230" dur="4s" repeatCount="indefinite"/></circle>
              </svg>
            </div>
          </div>
        </AnimatedSection>

        {/* Poster nav */}
        <div className="promo-nav">
          <button className="promo-narr" onClick={()=>goTo((posterIdx-1+POSTER_DATA.length)%POSTER_DATA.length)} aria-label="Prev">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="15 18 9 12 15 6"/></svg>
          </button>
          {POSTER_DATA.map((_,i)=>(
            <button key={i} className={`promo-ndot${i===posterIdx?' active':''}`} onClick={()=>goTo(i)} aria-label={`Slide ${i+1}`}/>
          ))}
          <button className="promo-narr" onClick={()=>goTo((posterIdx+1)%POSTER_DATA.length)} aria-label="Next">
            <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5"><polyline points="9 18 15 12 9 6"/></svg>
          </button>
        </div>

        {/* Video gallery */}
        {videos.length > 0 && (
          <AnimatedSection animation="reveal">
            <div style={{marginTop:64}}>
              <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:28,flexWrap:'wrap',gap:12}}>
                <div>
                  <div style={{display:'flex',alignItems:'center',gap:10}}>
                    <div style={{width:3,height:28,background:'#e8603a',borderRadius:2}}/>
                    <h3 style={{fontSize:'1.4rem',fontWeight:800,color:'white',letterSpacing:'-0.02em',margin:0}}>
                      Our Work <span style={{color:'#e8603a'}}>in Action</span>
                    </h3>
                  </div>
                  <p style={{color:'rgba(255,255,255,0.4)',fontSize:'0.82rem',marginTop:6,marginLeft:13}}>Hover to preview · Click to watch fullscreen</p>
                </div>
                <Link to="/portfolio" style={{display:'inline-flex',alignItems:'center',gap:6,color:'rgba(255,255,255,0.55)',fontSize:'0.85rem',fontWeight:600,textDecoration:'none'}}>
                  View All Projects
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
              </div>
              <div className="promo-vgrid">
                {videos.map(vid => {
                  const resolved = resolveUrl(vid.video_url);
                  const thumb    = vid.image_url ? (vid.image_url.startsWith('http') ? vid.image_url : `${apiBase}${vid.image_url}`) : null;
                  const isYtVm   = resolved.includes('youtube') || resolved.includes('vimeo');
                  return (
                    <div key={vid.id} className="promo-vc"
                      onMouseEnter={()=>!isYtVm&&handleVidHover(vid.id)}
                      onMouseLeave={()=>!isYtVm&&handleVidLeave(vid.id)}
                      onClick={()=>setLightbox({url:resolved,title:vid.title,isYtVm})}
                    >
                      {!isYtVm ? (
                        <video ref={el=>{if(el)vidRefs.current[vid.id]=el;}} src={resolved} muted playsInline preload="metadata" poster={thumb||undefined} style={{width:'100%',height:'100%',objectFit:'cover'}}/>
                      ) : thumb ? (
                        <img src={thumb} alt={vid.title} style={{width:'100%',height:'100%',objectFit:'cover'}} onError={e=>e.target.style.display='none'}/>
                      ) : (
                        <div style={{width:'100%',height:'100%',background:'linear-gradient(135deg,#16165a,#07071a)',display:'flex',alignItems:'center',justifyContent:'center'}}>
                          <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="rgba(255,255,255,0.18)" strokeWidth="1.5"><polygon points="5 3 19 12 5 21 5 3"/></svg>
                        </div>
                      )}
                      <div className="promo-vov">
                        <div className="promo-vpb"><svg viewBox="0 0 24 24" width="22" height="22" fill="white"><polygon points="5 3 19 12 5 21 5 3"/></svg></div>
                      </div>
                      <div className="promo-vexp">
                        <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="white" strokeWidth="2"><path d="M8 3H5a2 2 0 0 0-2 2v3m18 0V5a2 2 0 0 0-2-2h-3m0 18h3a2 2 0 0 0 2-2v-3M3 16v3a2 2 0 0 0 2 2h3"/></svg>
                      </div>
                      <div className="promo-vtit">
                        {vid.is_product && <span style={{display:'inline-block',background:'#e8603a',color:'white',fontSize:'0.6rem',fontWeight:800,padding:'2px 7px',borderRadius:50,marginBottom:4,marginRight:4}}>PRODUCT</span>}
                        {vid.title}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </AnimatedSection>
        )}

        {/* Empty state */}
        {videos.length === 0 && (
          <AnimatedSection animation="reveal">
            <div style={{marginTop:64,textAlign:'center',padding:'48px 24px',background:'rgba(255,255,255,0.02)',borderRadius:18,border:'1px dashed rgba(255,255,255,0.08)'}}>
              <svg viewBox="0 0 24 24" width="40" height="40" fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" style={{margin:'0 auto 14px',display:'block'}}><polygon points="5 3 19 12 5 21 5 3"/></svg>
              <p style={{color:'rgba(255,255,255,0.3)',fontSize:'0.9rem',margin:'0 0 14px'}}>
                No videos yet. Add a <strong style={{color:'#e8603a'}}>Video</strong> promotion from the admin panel, or enable videos in your portfolio with the <strong style={{color:'#e8603a'}}>Home</strong> toggle.
              </p>
              <div style={{display:'flex',gap:12,justifyContent:'center',flexWrap:'wrap'}}>
                <Link to="/admin" style={{display:'inline-flex',alignItems:'center',gap:6,color:'#e8603a',fontWeight:600,fontSize:'0.875rem',textDecoration:'none'}}>
                  Add Promotion Video
                  <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                </Link>
              </div>
            </div>
          </AnimatedSection>
        )}
      </div>

      {/* Lightbox */}
      {lightbox && (
        <div className="promo-lb" onClick={e=>e.target===e.currentTarget&&setLightbox(null)}>
          <button className="promo-lbc" onClick={()=>setLightbox(null)} aria-label="Close">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          </button>
          <div className="promo-lbi">
            {lightbox.isYtVm ? (
              <iframe src={getEmbed(lightbox.url)||lightbox.url} title={lightbox.title} allowFullScreen allow="autoplay"/>
            ) : (
              <video controls autoPlay style={{width:'100%',height:'100%',objectFit:'contain',background:'#000'}}>
                <source src={lightbox.url} type="video/mp4"/>
                <source src={lightbox.url} type="video/webm"/>
              </video>
            )}
          </div>
        </div>
      )}
    </section>
  );
}


/* ---- Client Logo Data ---- */
const clients = [
  { name: 'GLOBAL',              sub: 'Speciality Hospital',    tag: 'Healthcare',   color: '#e74c3c', accent: '#c0392b', desc: 'NABL Accredited Hospital, Trusted Care',    icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg> },
  { name: 'Sakhi',               sub: 'Welfare Society',        tag: 'NGO',          color: '#e91e63', accent: '#c2185b', desc: 'Empowering Women & Communities',            icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"/></svg> },
  { name: 'MEKAGRAVURE',         sub: 'Méka Studios',           tag: 'Creative',     color: '#2c3e50', accent: '#34495e', desc: 'Premium Engraving & Print Solutions',       icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/></svg> },
  { name: 'CA',                  sub: 'Chartered Associates',   tag: 'Finance',      color: '#7f8c8d', accent: '#636e72', desc: 'Certified Accounting & Tax Services',      icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg> },
  { name: 'THE PLANNING\nPARADISE', sub: 'Architecture & Design', tag: 'Real Estate', color: '#1a1a1a', accent: '#2d3436', desc: 'Innovative Architectural Planning',        icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="3 9 12 2 21 9 21 20 3 20 3 9"/></svg> },
  { name: 'WELL HOME',           sub: 'Media Studios',          tag: 'Media',        color: '#1a1a5e', accent: '#0a0a2e', desc: 'Creative Media & Film Production',          icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><polygon points="23 7 16 12 23 17 23 7"/><rect x="1" y="5" width="15" height="14" rx="2"/></svg> },
  { name: 'Wellcraft',           sub: 'Digital Solutions',      tag: 'Technology',   color: '#2980b9', accent: '#1a6fa3', desc: 'Precision Digital Craftsmanship',           icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><circle cx="12" cy="12" r="3"/><path d="M19.07 4.93A10 10 0 1 1 4.93 19.07 10 10 0 0 1 19.07 4.93"/></svg> },
  { name: 'RMS',                 sub: 'Rashtriya Military School', tag: 'Education', color: '#27ae60', accent: '#1e8449', desc: 'Excellence in Military Education',          icon: <svg viewBox="0 0 24 24" width="28" height="28" fill="none" stroke="currentColor" strokeWidth="1.8"><path d="M22 10v6M2 10l10-5 10 5-10 5z"/><path d="M6 12v5c3 3 9 3 12 0v-5"/></svg> },
];

const Home = () => {
  const [filter, setFilter] = useState('ALL');
  const [portfolioItems, setPortfolioItems] = useState(fallbackPortfolio);
  const [slideIndex, setSlideIndex] = useState(0);
  const [slideAnim, setSlideAnim] = useState(true);

  // Fetch portfolio from API — same source as Portfolio page
  useEffect(() => {
    fetch(`${API_BASE}/api/portfolio`)
      .then(r => { if (!r.ok) throw new Error('API error'); return r.json(); })
      .then(data => { if (data.success && data.data.length) setPortfolioItems(data.data); })
      .catch(() => setPortfolioItems(fallbackPortfolio));
  }, []);

  const filtered = filter === 'ALL' ? portfolioItems : portfolioItems.filter(p => p.category === filter);

  /* auto-advance slider */
  useEffect(() => {
    const t = setInterval(() => {
      setSlideAnim(false);
      setTimeout(() => {
        setSlideIndex(i => (i + 1) % slides.length);
        setSlideAnim(true);
      }, 80);
    }, 5000);
    return () => clearInterval(t);
  }, []);

  const goSlide = (idx) => {
    setSlideAnim(false);
    setTimeout(() => { setSlideIndex(idx); setSlideAnim(true); }, 80);
  };

  const prevSlide = () => goSlide((slideIndex - 1 + slides.length) % slides.length);
  const nextSlide = () => goSlide((slideIndex + 1) % slides.length);

  const current = slides[slideIndex];

  return (
    <div className="home-page">
      <style>{`
        /* ===== HERO SLIDER ===== */
        .hero-slider {
          min-height: 100vh;
          position: relative;
          overflow: hidden;
          display: flex;
          align-items: center;
          padding-top: 80px;
          padding-bottom: 70px; /* space for stats bar */
        }
        .hero-bg-img {
          position: absolute; inset: 0;
          background-size: cover;
          background-position: center;
          background-repeat: no-repeat;
          transition: opacity 0.6s ease;
          z-index: 0;
        }
        /* Overlay — darker at bottom-left where text is, clears toward top-right */
        .hero-bg-img::after {
          content: '';
          position: absolute; inset: 0;
          background:
            linear-gradient(
              to right,
              rgba(5,5,18,0.85) 0%,
              rgba(5,5,18,0.55) 45%,
              rgba(5,5,18,0.20) 70%,
              rgba(0,0,0,0.05) 100%
            ),
            linear-gradient(
              to top,
              rgba(0,0,0,0.65) 0%,
              rgba(0,0,0,0.25) 35%,
              transparent 65%
            );
        }
        .hero-slide-in { animation: slideHeroIn 0.7s cubic-bezier(0.22,1,0.36,1) both; }
        @keyframes slideHeroIn {
          from { opacity: 0; transform: translateY(calc(-50% + 20px)); }
          to   { opacity: 1; transform: translateY(-50%); }
        }

        /* ── Floating tech badges ── */
        /* Full screen width, below navbar — content box z-index:5 sits above */
        .hero-floats {
          position: absolute;
          top: 80px;
          left: 0;
          right: 0;
          bottom: 0;
          z-index: 2;
          pointer-events: none;
          overflow: hidden;
        }
        .flt-badge {
          position: absolute;
          display: flex; align-items: center; gap: 9px;
          background: rgba(7, 7, 26, 0.72);
          border: 1px solid rgba(255,255,255,0.14);
          backdrop-filter: blur(16px) saturate(180%);
          -webkit-backdrop-filter: blur(16px) saturate(180%);
          border-radius: 40px;
          padding: 8px 16px 8px 7px;
          color: white;
          font-size: 0.76rem; font-weight: 600;
          white-space: nowrap;
          box-shadow: 0 4px 20px rgba(0,0,0,0.4);
        }
        .flt-badge .fi {
          width: 28px; height: 28px; border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          flex-shrink: 0;
        }
        .flt-badge .fl { display: flex; flex-direction: column; line-height: 1.25; }
        .flt-badge .fl small { font-size: 0.62rem; opacity: 0.6; font-weight: 400; }

        /* icon ring colours */
        .ring-cyan   { background: rgba(79,195,247,0.18); border: 1.5px solid rgba(79,195,247,0.55); color: #4fc3f7; }
        .ring-orange { background: rgba(232,96,58,0.18);  border: 1.5px solid rgba(232,96,58,0.55);  color: #e8603a; }
        .ring-pink   { background: rgba(236,72,153,0.18); border: 1.5px solid rgba(236,72,153,0.55); color: #ec4899; }
        .ring-green  { background: rgba(34,197,94,0.18);  border: 1.5px solid rgba(34,197,94,0.55);  color: #22c55e; }
        .ring-purple { background: rgba(139,92,246,0.18); border: 1.5px solid rgba(139,92,246,0.55); color: #8b5cf6; }
        .ring-blue   { background: rgba(59,130,246,0.18); border: 1.5px solid rgba(59,130,246,0.55); color: #3b82f6; }

        /* badge: position + animation + left-border accent — all in one rule */
        @keyframes riseUp {
          0%   { top: 105%; opacity: 0; }
          6%   { opacity: 1; }
          94%  { opacity: 1; }
          100% { top: 0%;   opacity: 0; }
        }
        @keyframes fallDown {
          0%   { top: 0%;   opacity: 0; }
          6%   { opacity: 1; }
          94%  { opacity: 1; }
          100% { top: 105%; opacity: 0; }
        }

        /* Spread evenly across full screen width, below navbar */
        .fb1 { right: 72%; border-left: 2px solid #4fc3f7; animation: riseUp   14s linear infinite 0s;  }
        .fb2 { right:  4%; border-left: 2px solid #e8603a; animation: fallDown  13s linear infinite 2s;  }
        .fb3 { right: 38%; border-left: 2px solid #ec4899; animation: riseUp   11s linear infinite 4s;  }
        .fb4 { right: 20%; border-left: 2px solid #22c55e; animation: fallDown  15s linear infinite 1s;  }
        .fb5 { right: 56%; border-left: 2px solid #8b5cf6; animation: riseUp   12s linear infinite 6s;  }
        .fb6 { right: 10%; border-left: 2px solid #3b82f6; animation: fallDown  16s linear infinite 3s;  }

        /* subtle dot grid — less distracting than neon lines */
        .hero-grid-lines {
          position: absolute; inset: 0; z-index: 1; pointer-events: none;
          background-image: radial-gradient(rgba(255,255,255,0.06) 1px, transparent 1px);
          background-size: 40px 40px;
        }

        /* hero stats bar */
        .hero-stats-bar {
          position: absolute;
          bottom: 0; left: 0; right: 0;
          z-index: 5;
          background: rgba(5,5,18,0.72);
          backdrop-filter: blur(16px) saturate(160%);
          -webkit-backdrop-filter: blur(16px) saturate(160%);
          border-top: 1px solid rgba(255,255,255,0.07);
          display: flex;
          justify-content: center;
        }
        .hstat {
          flex: 1; max-width: 220px;
          padding: 18px 12px;
          text-align: center;
          border-right: 1px solid rgba(255,255,255,0.06);
          transition: background 0.2s;
        }
        .hstat:last-child { border-right: none; }
        .hstat:hover { background: rgba(232,96,58,0.07); }
        .hstat-num {
          font-size: 1.55rem; font-weight: 800;
          color: #e8603a; line-height: 1;
          letter-spacing: -0.03em;
        }
        .hstat-lbl {
          font-size: 0.68rem; color: rgba(255,255,255,0.45);
          text-transform: uppercase; letter-spacing: 0.1em;
          margin-top: 5px; font-weight: 600;
        }

        /* SVG graphic overlay — right side only */
        .hero-graphic-wrap {
          position: absolute;
          top: 50%; right: 4%;
          transform: translateY(-50%);
          z-index: 3;
          animation: float 5s ease-in-out infinite;
          opacity: 0.75;
          pointer-events: none;
        }

        /* watermark — subtle right side */
        .hero-watermark {
          position: absolute;
          right: 3%; bottom: 90px;
          font-size: clamp(2.5rem,7vw,5rem);
          font-weight: 900;
          line-height: 1;
          user-select: none;
          pointer-events: none;
          z-index: 2;
          animation: fadeInRight 1s ease 0.6s both;
        }
        .hero-watermark .w1 {
          display: block;
          color: transparent;
          -webkit-text-stroke: 1.5px rgba(79,195,247,0.25);
        }
        .hero-watermark .w2 {
          display: block;
          color: transparent;
          -webkit-text-stroke: 1.5px rgba(232,96,58,0.25);
        }

        /* hero content box — absolute left-center, no background, orange left line */
        .hero-content-box {
          position: absolute;
          left: clamp(60px, 7vw, 120px);
          top: 50%;
          transform: translateY(-50%);
          z-index: 5;
          max-width: 480px;
          padding: 0 0 0 28px;
          border-left: 4px solid #e8603a;
          background: none;
          backdrop-filter: none;
          -webkit-backdrop-filter: none;
          box-shadow: none;
          border-radius: 0;
        }
        .hero-content-box::before { display: none; }
        .hero-content-box h1 {
          font-size: clamp(1.7rem, 3.2vw, 2.5rem);
          font-weight: 800;
          color: #ffffff;
          margin-bottom: 16px;
          line-height: 1.18;
          letter-spacing: -0.02em;
          text-shadow: 0 2px 16px rgba(0,0,0,0.6);
        }
        .hero-content-box p {
          font-size: 0.95rem;
          color: rgba(255,255,255,0.88);
          line-height: 1.85;
          margin-bottom: 32px;
          font-weight: 400;
          max-width: 440px;
          text-shadow: 0 1px 8px rgba(0,0,0,0.5);
        }
        .hero-read-more {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 11px 26px;
          background: transparent;
          color: #fff;
          font-weight: 700;
          font-size: 0.875rem;
          letter-spacing: 0.03em;
          border-radius: 6px;
          text-decoration: none;
          border: 2px solid #e8603a;
          cursor: pointer;
          transition: background 0.22s ease, transform 0.22s ease, box-shadow 0.22s ease;
        }
        .hero-read-more:hover {
          background: #e8603a;
          transform: translateY(-2px);
          box-shadow: 0 6px 20px rgba(232,96,58,0.45);
        }
        .hero-read-more svg { flex-shrink: 0; transition: transform 0.22s ease; }
        .hero-read-more:hover svg { transform: translateX(3px); }

        /* prev/next arrows */
        .slider-arrow {
          position: absolute;
          top: 50%; transform: translateY(-50%);
          z-index: 5;
          background: rgba(255,255,255,0.07);
          border: 1px solid rgba(255,255,255,0.15);
          color: white;
          width: 42px; height: 42px;
          border-radius: 50%;
          display: flex; align-items: center; justify-content: center;
          cursor: pointer; font-size: 1.1rem;
          transition: all 0.25s ease;
          backdrop-filter: blur(8px);
        }
        .slider-arrow:hover {
          background: #e8603a;
          border-color: #e8603a;
          transform: translateY(-50%) scale(1.08);
          box-shadow: 0 4px 16px rgba(232,96,58,0.4);
        }
        .slider-arrow-left  { left: 20px; }
        .slider-arrow-right { right: 20px; }

        /* dots */
        .slider-dots {
          position: absolute;
          bottom: 76px; left: clamp(24px, 6vw, 100px);
          z-index: 6;
          display: flex; gap: 8px;
        }
        .slider-dot {
          width: 24px; height: 3px; border-radius: 2px;
          background: rgba(255,255,255,0.3);
          cursor: pointer; transition: all 0.3s ease;
        }
        .slider-dot.active {
          background: #e8603a;
          width: 40px;
        }

        /* ===== REST (unchanged) ===== */
        .stats-section {
          background: linear-gradient(135deg, #1a1a5e, #0a0a2e);
          padding: 80px 0;
          position: relative; overflow: hidden;
        }
        .stats-section::before {
          content: '';
          position: absolute; inset: 0;
          background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Ccircle cx='30' cy='30' r='2'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
        }
        .stats-banner { text-align: center; margin-bottom: 50px; position: relative; z-index: 1; }
        .stats-banner h2 { font-size: 2rem; font-weight: 800; color: white; }
        .stats-banner h2 span { color: #ff6b35; }
        .stats-banner p { color: rgba(255,255,255,0.6); margin-top: 10px; }
        .services-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 28px; }
        .service-card {
          background: white; border-radius: 16px; padding: 34px 28px;
          text-align: center; border: 1px solid #e9ecef;
          transition: all 0.4s ease; position: relative; overflow: hidden; cursor: default;
        }
        .service-card::before {
          content: ''; position: absolute; top: 0; left: 0; right: 0; height: 4px;
          background: linear-gradient(90deg, #1a1a5e, #ff6b35);
          transform: scaleX(0); transition: transform 0.4s ease;
        }
        .service-card:hover::before { transform: scaleX(1); }
        .service-card:hover { transform: translateY(-10px); box-shadow: 0 20px 50px rgba(26,26,94,0.15); border-color: transparent; }
        .service-icon {
          width: 70px; height: 70px;
          background: linear-gradient(135deg, #f0f2ff, #e8eaff);
          border-radius: 18px; display: flex; align-items: center; justify-content: center;
          font-size: 1.8rem; margin: 0 auto 20px; transition: all 0.4s ease;
        }
        .service-card:hover .service-icon { background: linear-gradient(135deg, #1a1a5e, #2d2d8e); transform: scale(1.1) rotate(5deg); }
        .service-card h3 { font-size: 1.05rem; font-weight: 700; color: #1a1a5e; margin-bottom: 12px; }
        .service-card p { font-size: 0.88rem; color: #6c757d; line-height: 1.7; }
        .portfolio-section { background: #f8f9fa; }
        .portfolio-filters { display: flex; justify-content: center; gap: 12px; margin-bottom: 40px; flex-wrap: wrap; }
        .filter-btn {
          padding: 10px 28px; border-radius: 50px; font-size: 0.88rem; font-weight: 600;
          border: 2px solid #dee2e6; background: white; color: #6c757d;
          cursor: pointer; transition: all 0.3s ease; letter-spacing: 0.5px;
        }
        .filter-btn.active, .filter-btn:hover {
          background: linear-gradient(135deg, #1a1a5e, #2d2d8e);
          border-color: #1a1a5e; color: white;
          transform: translateY(-2px); box-shadow: 0 6px 20px rgba(26,26,94,0.3);
        }
        .portfolio-grid {
          display: grid;
          grid-template-columns: repeat(3, 1fr);
          gap: 24px;
          align-items: stretch;
        }
        /* Ensure the AnimatedSection wrapper and Link fill full height */
        .portfolio-grid > div,
        .portfolio-grid > div > a {
          height: 100%;
          display: block;
        }
        .portfolio-card {
          border-radius: 14px; overflow: hidden; background: white;
          box-shadow: 0 4px 15px rgba(0,0,0,0.08);
          transition: all 0.4s ease; cursor: pointer;
          display: flex; flex-direction: column;
          height: 100%;
        }
        .portfolio-card:hover { transform: translateY(-8px); box-shadow: 0 16px 40px rgba(0,0,0,0.15); }
        .portfolio-img {
          height: 180px; display: flex; align-items: center; justify-content: center;
          font-size: 3.5rem; position: relative; overflow: hidden;
          transition: all 0.4s ease; flex-shrink: 0;
        }
        .portfolio-overlay {
          position: absolute; inset: 0; background: rgba(0,0,0,0.7);
          display: flex; align-items: center; justify-content: center;
          opacity: 0; transition: all 0.4s ease;
        }
        .portfolio-card:hover .portfolio-overlay { opacity: 1; }
        .portfolio-overlay-text {
          color: white; font-weight: 600; font-size: 0.9rem; letter-spacing: 1px;
          border: 2px solid white; padding: 8px 20px; border-radius: 50px;
          transform: translateY(10px); transition: transform 0.4s ease;
        }
        .portfolio-card:hover .portfolio-overlay-text { transform: translateY(0); }
        .portfolio-info {
          padding: 20px;
          display: flex; flex-direction: column; flex: 1;
        }
        .portfolio-cat {
          display: inline-block; background: linear-gradient(135deg, #ff6b35, #ff8c5a);
          color: white; padding: 3px 12px; border-radius: 50px; font-size: 0.72rem;
          font-weight: 700; letter-spacing: 1px; margin-bottom: 10px; align-self: flex-start;
        }
        .portfolio-info h3 {
          font-size: 0.95rem; font-weight: 700; color: #1a1a5e;
          margin-bottom: 8px; line-height: 1.3;
        }
        .portfolio-info p {
          font-size: 0.82rem; color: #6c757d; line-height: 1.65;
          /* Clamp to 3 lines */
          display: -webkit-box;
          -webkit-line-clamp: 3;
          -webkit-box-orient: vertical;
          overflow: hidden;
          flex: 1; /* push read-more to bottom */
          margin-bottom: 8px;
        }
        .portfolio-read-more {
          font-size: 0.78rem; color: #ff6b35; font-weight: 600;
          text-decoration: none; display: inline-flex; align-items: center; gap: 4px;
          margin-top: auto; /* stick to bottom */
          transition: color 0.2s ease;
        }
        .portfolio-read-more:hover { color: #e8603a; }
        .clients-section { padding: 80px 0; background: #f8f9fa; }
        .clients-grid-new {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 24px;
          margin-top: 20px;
        }
        .client-card {
          background: white;
          border-radius: 16px;
          border: 1px solid #e9ecef;
          overflow: hidden;
          transition: all 0.4s cubic-bezier(0.175,0.885,0.32,1.275);
          cursor: default;
          position: relative;
        }
        .client-card:hover {
          transform: translateY(-10px) scale(1.03);
          box-shadow: 0 22px 55px rgba(0,0,0,0.14);
          border-color: transparent;
        }
        .client-card-bar {
          height: 5px;
          transition: height 0.3s ease;
        }
        .client-card:hover .client-card-bar { height: 8px; }
        .client-card-body {
          padding: 22px 18px 20px;
          display: flex; flex-direction: column;
          align-items: center; text-align: center; gap: 8px;
        }
        .client-icon-wrap {
          width: 62px; height: 62px; border-radius: 14px;
          display: flex; align-items: center; justify-content: center;
          font-size: 1.9rem;
          transition: all 0.4s ease;
          position: relative; overflow: hidden;
        }
        .client-card:hover .client-icon-wrap {
          transform: rotate(-6deg) scale(1.18);
          box-shadow: 0 8px 24px rgba(0,0,0,0.18);
        }
        .client-icon-wrap::after {
          content: '';
          position: absolute; top: -50%; left: -60%;
          width: 40%; height: 200%;
          background: rgba(255,255,255,0.38);
          transform: skewX(-20deg);
          transition: transform 0.55s ease;
        }
        .client-card:hover .client-icon-wrap::after { transform: skewX(-20deg) translateX(600%); }
        .client-tag {
          display: inline-block; padding: 3px 11px;
          border-radius: 50px; font-size: 0.66rem;
          font-weight: 700; letter-spacing: 0.5px; text-transform: uppercase;
        }
        .client-name {
          font-size: 0.98rem; font-weight: 800; color: #1a1a5e;
          line-height: 1.25; white-space: pre-line;
          transition: color 0.3s ease;
        }
        .client-card:hover .client-name { color: #ff6b35; }
        .client-sub { font-size: 0.76rem; color: #6c757d; font-weight: 500; }
        .client-desc {
          font-size: 0.73rem; color: #adb5bd; line-height: 1.5;
          opacity: 0; max-height: 0; overflow: hidden;
          transition: all 0.4s ease;
        }
        .client-card:hover .client-desc { opacity: 1; max-height: 40px; }
        /* subtle glow behind card on hover */
        .client-card::before {
          content: ''; position: absolute; inset: -1px;
          border-radius: 17px; opacity: 0;
          transition: opacity 0.4s ease; z-index: -1;
          background: linear-gradient(135deg, var(--cc,#1a1a5e), transparent);
        }
        .client-card:hover::before { opacity: 0.07; }
        @media (max-width: 1024px) { .clients-grid-new { grid-template-columns: repeat(3,1fr); } }
        @media (max-width: 768px)  { .clients-grid-new { grid-template-columns: repeat(2,1fr); } }
        @media (max-width: 480px)  { .clients-grid-new { grid-template-columns: repeat(2,1fr); gap: 14px; } }
        .cta-section {
          background: linear-gradient(135deg, #050516, #1a1a5e, #2d1b69);
          padding: 100px 20px; text-align: center; position: relative; overflow: hidden;
        }
        .cta-section::before {
          content: ''; position: absolute;
          width: 500px; height: 500px;
          background: radial-gradient(circle, rgba(0,212,255,0.08), transparent);
          border-radius: 50%; top: 50%; left: 50%; transform: translate(-50%,-50%);
          animation: pulse 4s ease infinite;
        }
        .cta-section h2 { font-size: 2.8rem; font-weight: 800; color: white; margin-bottom: 20px; position: relative; z-index: 1; }
        .cta-section h2 span { color: #ff6b35; }
        .cta-section p { color: rgba(255,255,255,0.7); font-size: 1.05rem; max-width: 600px; margin: 0 auto 36px; line-height: 1.8; position: relative; z-index: 1; }
        .cta-buttons { display: flex; gap: 16px; justify-content: center; flex-wrap: wrap; position: relative; z-index: 1; }

        @media (max-width: 1024px) {
          .services-grid, .portfolio-grid { grid-template-columns: repeat(2, 1fr); }
          .hero-graphic-wrap { display: none; }
          .hero-watermark { display: none; }
          /* On tablets widen the content box a bit */
          .hero-content-box { max-width: 420px; }
        }
        @media (max-width: 768px) {
          /* On mobile switch from absolute centering to normal flow */
          .hero-content-box {
            position: absolute;
            left: 16px;
            right: 16px;
            top: 50%;
            transform: translateY(-50%);
            max-width: calc(100% - 32px);
            padding-left: 20px;
          }
          .hero-content-box h1 { font-size: 1.45rem; }
          .hero-slide-in { animation: slideHeroInMobile 0.7s cubic-bezier(0.22,1,0.36,1) both; }
          @keyframes slideHeroInMobile {
            from { opacity: 0; transform: translateY(calc(-50% + 16px)); }
            to   { opacity: 1; transform: translateY(-50%); }
          }
          .hero-floats { left: 0; top: 80px; }
          .hero-stats-bar { flex-wrap: wrap; }
          .hstat { min-width: 50%; border-bottom: 1px solid rgba(255,255,255,0.07); }
          .services-grid { grid-template-columns: 1fr 1fr; }
          .portfolio-grid { grid-template-columns: 1fr 1fr; }
          .cta-section h2 { font-size: 2rem; }
        }
        @media (max-width: 480px) {
          .services-grid, .portfolio-grid { grid-template-columns: 1fr; }
          .hero-content-box h1 { font-size: 1.3rem; }
        }
      `}</style>

      {/* ===== HERO SLIDER ===== */}
      <section className="hero-slider">

        {/* Background photo */}
        <div
          className="hero-bg-img"
          style={{ backgroundImage: `url(${current.bg})` }}
        />

        {/* Moving grid */}
        <div className="hero-grid-lines" />

        /* Floating tech icon badges */
        <div className="hero-floats">
          <div className="flt-badge fb1">
            <div className="fi ring-cyan">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>
            </div>
            <div className="fl"><span>Web Development</span><small>HTML · CSS · JS</small></div>
          </div>
          <div className="flt-badge fb2">
            <div className="fi ring-orange">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="3"/><path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/></svg>
            </div>
            <div className="fl"><span>UI / UX Design</span><small>Figma · Photoshop</small></div>
          </div>
          <div className="flt-badge fb3">
            <div className="fi ring-pink">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><polyline points="22 7 13.5 15.5 8.5 10.5 2 17"/><polyline points="16 7 22 7 22 13"/></svg>
            </div>
            <div className="fl"><span>Digital Marketing</span><small>SEO · SMM</small></div>
          </div>
          <div className="flt-badge fb4">
            <div className="fi ring-green">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="9" cy="21" r="1"/><circle cx="20" cy="21" r="1"/><path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"/></svg>
            </div>
            <div className="fl"><span>E-Commerce</span><small>WooCommerce · Shopify</small></div>
          </div>
          <div className="flt-badge fb5">
            <div className="fi ring-purple">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><rect x="5" y="2" width="14" height="20" rx="2"/><line x1="12" y1="18" x2="12.01" y2="18"/></svg>
            </div>
            <div className="fl"><span>App Development</span><small>Android · iOS</small></div>
          </div>
          <div className="flt-badge fb6">
            <div className="fi ring-blue">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
            </div>
            <div className="fl"><span>SEO Optimization</span><small>Google · Bing</small></div>
          </div>
        </div>

        {/* SVG Graphic overlay */}
        <div className="hero-graphic-wrap">
          {current.graphic === 'browser'   && <BrowserGraphic />}
          {current.graphic === 'wireframe' && <WireframeGraphic />}
          {current.graphic === 'code'      && <CodeGraphic />}
        </div>

        {/* WEBSITE watermark */}
        <div className="hero-watermark">
          <span className="w1">WEB</span>
          <span className="w2">SITE</span>
        </div>

        {/* Content box */}
        <div className={`hero-content-box ${slideAnim ? 'hero-slide-in' : ''}`}>
          <h1>{current.title}</h1>
          <p>{current.desc}</p>
          <Link to="/contact" className="hero-read-more">
            Read More
            <svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
          </Link>
        </div>

        {/* Prev / Next arrows */}
        <button className="slider-arrow slider-arrow-left" onClick={prevSlide} aria-label="Previous slide">&#8249;</button>
        <button className="slider-arrow slider-arrow-right" onClick={nextSlide} aria-label="Next slide">&#8250;</button>

        {/* Dot indicators */}
        <div className="slider-dots">
          {slides.map((_, i) => (
            <div
              key={i}
              className={`slider-dot ${i === slideIndex ? 'active' : ''}`}
              onClick={() => goSlide(i)}
            />
          ))}
        </div>

        {/* Stats bar at bottom */}
        <div className="hero-stats-bar">
          {[
            { num: '100+', lbl: 'Websites Built' },
            { num: '50+',  lbl: 'Happy Clients'  },
            { num: '5+',   lbl: 'Years Experience'},
            { num: '200+', lbl: 'Projects Done'   },
          ].map((s, i) => (
            <div key={i} className="hstat">
              <div className="hstat-num">{s.num}</div>
              <div className="hstat-lbl">{s.lbl}</div>
            </div>
          ))}
        </div>
      </section>

      {/* STATS */}
      <section className="stats-section">
        <div className="container">
          <AnimatedSection className="stats-banner">
            <h2>We've created more than <span>100 websites</span> this year!</h2>
            <p>Trusted by businesses across India for quality digital solutions</p>
          </AnimatedSection>
          <div className="stats-grid" style={{ position: 'relative', zIndex: 1 }}>
            <CounterCard target={100} label="Websites Built" icon={<svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#ff6b35" strokeWidth="1.8"><rect x="2" y="3" width="20" height="14" rx="2"/><path d="M8 21h8M12 17v4"/></svg>} suffix="+" delay={0} />
            <CounterCard target={50} label="Happy Clients" icon={<svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#ff6b35" strokeWidth="1.8"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg>} suffix="+" delay={200} />
            <CounterCard target={5} label="Years Experience" icon={<svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#ff6b35" strokeWidth="1.8"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>} suffix="+" delay={400} />
            <CounterCard target={200} label="Projects Done" icon={<svg viewBox="0 0 24 24" width="32" height="32" fill="none" stroke="#ff6b35" strokeWidth="1.8"><polyline points="9 11 12 14 22 4"/><path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/></svg>} suffix="+" delay={600} />
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="section-padding">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">What We Do</span>
            <h2 className="section-title">Our <span>Services</span></h2>
            <div className="section-divider">
              <span></span><span></span><span></span>
            </div>
            <p className="section-subtitle">
              We offer comprehensive digital services to help your business grow and succeed in the digital world.
            </p>
          </AnimatedSection>
          <div className="services-grid">
            {services.map((s, i) => (
              <AnimatedSection key={i} animation="reveal" delay={i * 100}>
                <div className="service-card">
                  <div className="service-icon">{s.icon}</div>
                  <h3>{s.title}</h3>
                  <p>{s.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/services" className="btn btn-dark">View All Services →</Link>
          </div>
        </div>
      </section>

      {/* PORTFOLIO */}
      <section className="section-padding portfolio-section">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">Our Work</span>
            <h2 className="section-title">Recent <span>Portfolio</span></h2>
            <div className="section-divider">
              <span></span><span></span><span></span>
            </div>
            <p className="section-subtitle">
              Explore our recent projects and see the quality of work we deliver to our valued clients.
            </p>
          </AnimatedSection>
          <div className="portfolio-filters">
            {['ALL', 'WEB', 'APP'].map(f => (
              <button
                key={f}
                className={`filter-btn ${filter === f ? 'active' : ''}`}
                onClick={() => setFilter(f)}
              >
                {f}
              </button>
            ))}
          </div>
          <div className="portfolio-grid">
            {filtered.slice(0, 6).map((item, i) => (
              <AnimatedSection key={item.id} animation="reveal" delay={i * 80} style={{ height:'100%' }}>
                <Link to={`/portfolio/${item.id}`} style={{ textDecoration:'none', color:'inherit', display:'block', height:'100%' }}>
                  <div className="portfolio-card">
                    <div className="portfolio-img" style={{ background:`linear-gradient(135deg, ${item.color||'#3498db'}22, ${item.color||'#3498db'}44)` }}>
                      {item.image_url ? (
                        <img
                          src={item.image_url.startsWith('http') ? item.image_url : `${API_BASE}${item.image_url}`}
                          alt={item.title}
                          style={{ position:'absolute', inset:0, width:'100%', height:'100%', objectFit:'cover' }}
                          onError={e => { e.target.style.display='none'; }}
                        />
                      ) : (
                        <span>{item.icon}</span>
                      )}
                      <div className="portfolio-overlay">
                        <span className="portfolio-overlay-text">View Project</span>
                      </div>
                    </div>
                    <div className="portfolio-info">
                      <span className="portfolio-cat">{item.category}</span>
                      <h3>{item.title}</h3>
                      <p>{item.description || item.desc}</p>
                      <span className="portfolio-read-more">
                        Read more
                        <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
                      </span>
                    </div>
                  </div>
                </Link>
              </AnimatedSection>
            ))}
          </div>
          <div className="text-center mt-4">
            <Link to="/portfolio" className="btn btn-outline">View All Projects →</Link>
          </div>
        </div>
      </section>

      {/* CLIENTS */}
      <section className="clients-section">
        <div className="container">
          <AnimatedSection className="section-header">
            <span className="section-tag">Trusted By</span>
            <h2 className="section-title">Our <span>Clients</span></h2>
            <div className="section-divider"><span></span><span></span><span></span></div>
            <p className="section-subtitle" style={{ maxWidth: '820px' }}>
              A true partnership is a two-way street — ideas and information flow openly and regularly,
              based on a foundation of mutual trust and respect. Our client list speaks for itself.
              Since our earliest days, we've represented everything from start-ups to established enterprises.
            </p>
          </AnimatedSection>
          <div className="clients-grid-new">
            {clients.map((c, i) => (
              <AnimatedSection key={i} animation="reveal" delay={i * 80}>
                <div className="client-card" style={{ '--cc': c.color }}>
                  <div className="client-card-bar" style={{ background: `linear-gradient(90deg, ${c.color}, ${c.accent})` }} />
                  <div className="client-card-body">
                    <div className="client-icon-wrap" style={{ background: `linear-gradient(135deg, ${c.color}22, ${c.color}44)` }}>
                      {c.icon}
                    </div>
                    <span className="client-tag" style={{ background: `${c.color}18`, color: c.color }}>
                      {c.tag}
                    </span>
                    <div className="client-name">{c.name}</div>
                    <div className="client-sub">{c.sub}</div>
                    <div className="client-desc">{c.desc}</div>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* ── PROMOTIONS SECTION ── */}
      <PromotionSection apiBase={API_BASE} />

      {/* ── Office / Life at Saanvi strip ── */}
      {/* <section style={{ background: '#07071a', padding: '96px 0', overflow: 'hidden' }}>
        <div className="container">
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:64, alignItems:'center' }}> */}
            {/* Left: text */}
            {/* <AnimatedSection animation="reveal-left">
              <span style={{ display:'inline-block', background:'rgba(232,96,58,0.1)', color:'#e8603a', border:'1px solid rgba(232,96,58,0.2)', padding:'5px 16px', borderRadius:50, fontSize:'0.72rem', fontWeight:700, letterSpacing:'0.1em', textTransform:'uppercase', marginBottom:18 }}>
                Our Office
              </span>
              <h2 style={{ fontSize:'clamp(1.6rem,3vw,2.4rem)', fontWeight:800, color:'white', letterSpacing:'-0.03em', lineHeight:1.15, marginBottom:20 }}>
                A Team That Builds <span style={{ color:'#e8603a' }}>Great Things</span>
              </h2>
              <p style={{ color:'rgba(255,255,255,0.55)', lineHeight:1.9, fontSize:'0.95rem', marginBottom:28 }}>
                From our office in Gwalior, our dedicated team of developers, designers and digital marketers collaborates every day to deliver exceptional results for clients across India. We believe great software is built by great people.
              </p>
              <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:32 }}>
                {[
                  'Dedicated workspace in Thatipur, Gwalior',
                  '15+ skilled developers, designers & marketers',
                  'Mon – Sat: 9 AM to 7 PM, always reachable',
                  '200+ projects delivered since 2021',
                ].map((item, i) => (
                  <div key={i} style={{ display:'flex', alignItems:'center', gap:12 }}>
                    <div style={{ width:20, height:20, borderRadius:50, background:'rgba(232,96,58,0.15)', border:'1px solid rgba(232,96,58,0.35)', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="#e8603a" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                    </div>
                    <span style={{ fontSize:'0.875rem', color:'rgba(255,255,255,0.75)', fontWeight:500 }}>{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" style={{ display:'inline-flex', alignItems:'center', gap:8, padding:'12px 26px', background:'transparent', border:'1.5px solid rgba(255,255,255,0.25)', color:'white', borderRadius:9999, fontWeight:600, fontSize:'0.875rem', textDecoration:'none', transition:'all 0.22s ease' }}
                onMouseEnter={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,0.6)'; e.currentTarget.style.background='rgba(255,255,255,0.07)';}}
                onMouseLeave={e=>{e.currentTarget.style.borderColor='rgba(255,255,255,0.25)'; e.currentTarget.style.background='transparent';}}
              >
                Meet Our Team
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.5"><line x1="5" y1="12" x2="19" y2="12"/><polyline points="12 5 19 12 12 19"/></svg>
              </Link>
            </AnimatedSection> */}

            {/* Right: 2x2 photo grid */}
            {/* <AnimatedSection animation="reveal-right">
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10 }}>
                {[
                  { src:'/images/office/office-1.jpeg', alt:'Team members working at desks', style:{ borderRadius:'14px 14px 4px 14px' } },
                  { src:'/images/office/office-2.jpeg', alt:'Development team collaborating', style:{ borderRadius:'14px 14px 14px 4px', marginTop:24 } },
                  { src:'/images/office/office-4.jpeg', alt:'Saanvi Innovation workspace', style:{ borderRadius:'14px 4px 14px 14px', marginTop:-24 } },
                  { src:'/images/office/office-5.jpeg', alt:'Engineers working on laptops', style:{ borderRadius:'4px 14px 14px 14px' } },
                ].map((img, i) => (
                  <div key={i} style={{ overflow:'hidden', boxShadow:'0 8px 28px rgba(0,0,0,0.45)', ...img.style }}>
                    <img
                      src={img.src}
                      alt={img.alt}
                      style={{ width:'100%', height:200, objectFit:'cover', display:'block', transition:'transform 0.5s ease' }}
                      onMouseEnter={e=>e.target.style.transform='scale(1.06)'}
                      onMouseLeave={e=>e.target.style.transform='scale(1)'}
                      onError={e=>{
                        const fallbacks = [
                          'https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=600&q=80',
                          'https://images.unsplash.com/photo-1497366216548-37526070297c?w=600&q=80',
                          'https://images.unsplash.com/photo-1504384308090-c894fdcc538d?w=600&q=80',
                          'https://images.unsplash.com/photo-1560472355-536de3962603?w=600&q=80',
                        ];
                        e.target.src = fallbacks[i];
                      }}
                    />
                  </div>
                ))}
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section> */}

      {/* CTA */}
      <section className="cta-section">
        <AnimatedSection>
          <h2>Ready to Start Your <span>Project?</span></h2>
          <p>
            Let's work together to build something amazing. Get a free consultation
            and quote for your next digital project.
          </p>
          <div className="cta-buttons">
            <Link to="/contact#quote" className="btn btn-primary">
              <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" style={{marginRight:6, flexShrink:0}}><line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/></svg>
              Request a Quote
            </Link>
            <Link to="/faq" className="btn btn-secondary">View FAQ</Link>
          </div>
        </AnimatedSection>
      </section>
    </div>
  );
};

export default Home;
